import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Building2, FileText, Home, Sparkles, CalendarCheck, BookOpen, Bell, Library,
  LogOut, ChevronDown, Check, Users, FolderOpen, Shield, Menu, X, KeyRound,
  Pencil, Archive, FileType, Calculator, Database, Send,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

// Feature flags — flip to true and redeploy to re-expose these items.
// (Schedule III + Tally Sync are temporarily hidden in nav per product
// decision on 2026-02-04; routes still resolve via direct URL.)
const SHOW_SCHEDULE3_AND_TALLY = false;

// Flat list of every destination, plus its parent group key (or "" if standalone).
// Single source of truth — used by both desktop dropdowns and mobile drawer.
const NAV_ITEMS = [
  { to: "/", label: "Dashboard", icon: Home, testId: "nav-dashboard", group: "" },
  { to: "/companies", label: "Companies", icon: Building2, testId: "nav-companies", group: "" },

  // Drafting group — anything that creates a document
  { to: "/generate", label: "Generate", icon: Sparkles, testId: "nav-generate", group: "drafting" },
  { to: "/resolutions", label: "Resolutions", icon: FileText, testId: "nav-resolutions", group: "drafting" },
  { to: "/templates", label: "Templates", icon: FileType, testId: "nav-templates", group: "drafting" },
  // Schedule III + Tally Sync — temporarily hidden (set to true after the
  // next production deploy). Routes still work via direct URL.
  ...(SHOW_SCHEDULE3_AND_TALLY ? [
    { to: "/schedule3", label: "Schedule III", icon: Calculator, testId: "nav-schedule3", group: "drafting" },
    { to: "/integrations/tally", label: "Tally Sync", icon: Database, testId: "nav-tally", group: "drafting" },
  ] : []),
  { to: "/procedures", label: "Procedures", icon: BookOpen, testId: "nav-procedures", group: "drafting" },
  { to: "/style-library", label: "Style Library", icon: Library, testId: "nav-style-library", group: "drafting" },

  // Records group — anything that stores existing data
  { to: "/vault", label: "Vault", icon: FolderOpen, testId: "nav-vault", group: "records" },
  { to: "/dsc-book", label: "DSC Book", icon: KeyRound, testId: "nav-dsc-book", group: "records" },
  { to: "/calendar", label: "Calendar", icon: CalendarCheck, testId: "nav-calendar", group: "records" },
  { to: "/efiling", label: "E-Filing", icon: Send, testId: "nav-efiling", group: "records" },

  { to: "/notifications", label: "Updates", icon: Bell, testId: "nav-notifications", group: "" },
];

// Ordered top-level structure for the desktop bar:
//   [{ kind: "tab", item }, { kind: "group", key, label, icon, items: [...] }, ...]
const DESKTOP_STRUCTURE = [
  { kind: "tab", item: NAV_ITEMS.find((t) => t.to === "/") },
  { kind: "tab", item: NAV_ITEMS.find((t) => t.to === "/companies") },
  {
    kind: "group", key: "drafting", label: "Drafting", icon: Pencil,
    items: NAV_ITEMS.filter((t) => t.group === "drafting"),
  },
  {
    kind: "group", key: "records", label: "Records", icon: Archive,
    items: NAV_ITEMS.filter((t) => t.group === "records"),
  },
  { kind: "tab", item: NAV_ITEMS.find((t) => t.to === "/notifications") },
];

function DesktopNavTab({ item, onActivate }) {
  const location = useLocation();
  const Icon = item.icon;
  const active =
    location.pathname === item.to || (item.to !== "/" && location.pathname.startsWith(item.to));
  return (
    <NavLink
      to={item.to}
      data-testid={item.testId}
      onClick={onActivate}
      onMouseEnter={onActivate}
      className={`px-3 py-2 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors ${
        active
          ? "border-[#002FA7] text-[#002FA7]"
          : "border-transparent text-zinc-700 hover:text-[#002FA7]"
      }`}
    >
      <Icon size={15} strokeWidth={1.75} />
      {item.label}
    </NavLink>
  );
}

function DesktopNavGroup({ group, openKey, setOpenKey }) {
  const location = useLocation();
  const Icon = group.icon;
  const open = openKey === group.key;
  const activeChild = group.items.find((c) => location.pathname.startsWith(c.to));
  const active = !!activeChild;

  return (
    <div className="relative">
      <button
        onClick={() => setOpenKey(open ? null : group.key)}
        onMouseEnter={() => {
          // If another menu is currently open, switch to this one on hover —
          // standard menu-bar behaviour. We do NOT open on hover from idle.
          setOpenKey((k) => (k && k !== group.key ? group.key : k));
        }}
        data-testid={`nav-group-${group.key}`}
        className={`px-3 py-2 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors ${
          active
            ? "border-[#002FA7] text-[#002FA7]"
            : "border-transparent text-zinc-700 hover:text-[#002FA7]"
        }`}
      >
        <Icon size={15} strokeWidth={1.75} />
        {group.label}
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          className="absolute left-0 top-full mt-1 w-56 border border-zinc-200 bg-white shadow-lg z-50"
          data-testid={`nav-group-${group.key}-menu`}
        >
          {group.items.map((child) => {
            const ChildIcon = child.icon;
            const childActive = location.pathname.startsWith(child.to);
            return (
              <NavLink
                key={child.to}
                to={child.to}
                onClick={() => setOpenKey(null)}
                data-testid={child.testId}
                className={`flex items-center gap-2.5 px-4 py-2.5 text-sm hover:bg-zinc-50 transition-colors ${
                  childActive ? "text-[#002FA7] font-medium bg-blue-50/40" : "text-zinc-700"
                }`}
              >
                <ChildIcon size={14} strokeWidth={1.75} />
                {child.label}
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function NavBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, firm, firms, logout, switchFirm, isSuperAdmin } = useAuth();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Single source of truth for ALL header menus (Drafting, Records,
  // profile, firm-switcher). Opening any one closes the others.
  // Values: "drafting" | "records" | "user" | "firm" | null
  const [openMenu, setOpenMenu] = useState(null);

  // Close any open dropdown when the route changes.
  useEffect(() => {
    setMobileNavOpen(false);
    setOpenMenu(null);
  }, [location.pathname]);

  // Close any open dropdown when clicking outside the header.
  const headerRef = useRef(null);
  useEffect(() => {
    if (!openMenu) return undefined;
    const onClick = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target)) setOpenMenu(null);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [openMenu]);

  const handleLogout = async () => {
    setOpenMenu(null);
    await logout();
    navigate("/login", { replace: true });
  };

  const handleSwitchFirm = async (firmId) => {
    if (firmId === firm?.firm_id) {
      setOpenMenu(null);
      return;
    }
    try {
      await switchFirm(firmId);
      toast.success("Workspace switched");
      setOpenMenu(null);
      navigate(0);
    } catch {
      toast.error("Could not switch workspace");
    }
  };

  const hasMultipleFirms = (firms || []).length > 1;

  return (
    <header ref={headerRef} className="border-b border-zinc-200 bg-white sticky top-0 z-40" data-testid="app-header">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="brand-link">
          <div className="w-9 h-9 bg-[#002FA7] flex items-center justify-center">
            <span className="text-white font-heading font-bold text-base leading-none">ROC</span>
          </div>
          <div>
            <div className="font-heading font-medium tracking-tight text-base leading-none">ROC Vault</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">
              MCA Compliance · India
            </div>
          </div>
        </Link>

        {/* Multi-firm switcher */}
        {user && hasMultipleFirms && (
          <div className="relative">
            <button
              onClick={() => setOpenMenu((m) => (m === "firm" ? null : "firm"))}
              className="flex items-center gap-2 border border-zinc-200 px-3 py-1.5 text-xs font-medium hover:border-[#002FA7] transition-colors"
              data-testid="firm-switcher-button"
            >
              <Building2 size={12} className="text-[#002FA7]" />
              <span className="truncate max-w-[140px]">{firm?.name || "Select workspace"}</span>
              <ChevronDown size={11} className="text-zinc-500" />
            </button>
            {openMenu === "firm" && (
              <div className="absolute left-0 top-full mt-1 w-72 border border-zinc-200 bg-white shadow-lg z-50" data-testid="firm-switcher-menu">
                <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-100">
                  Switch workspace
                </div>
                {(firms || []).map((f) => (
                  <button
                    key={f.firm_id}
                    onClick={() => handleSwitchFirm(f.firm_id)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-zinc-50 flex items-center justify-between"
                    data-testid={`firm-option-${f.firm_id}`}
                  >
                    <span className="truncate">{f.name}</span>
                    {f.firm_id === firm?.firm_id && <Check size={12} className="text-[#002FA7] shrink-0" />}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Desktop nav — 5 top-level items (Dashboard · Companies · Drafting ▾ · Records ▾ · Updates) */}
        <nav className="hidden md:flex items-center gap-1" data-testid="main-nav">
          {DESKTOP_STRUCTURE.map((entry) =>
            entry.kind === "tab" ? (
              <DesktopNavTab
                key={entry.item.to}
                item={entry.item}
                onActivate={() => setOpenMenu(null)}
              />
            ) : (
              <DesktopNavGroup
                key={entry.key}
                group={entry}
                openKey={openMenu}
                setOpenKey={setOpenMenu}
              />
            )
          )}
        </nav>

        {user && (
          <div className="flex items-center gap-2 shrink-0">
            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileNavOpen((o) => !o)}
              className="md:hidden p-2 border border-zinc-200 hover:border-[#002FA7] transition-colors"
              data-testid="mobile-nav-toggle"
              aria-label="Open navigation"
            >
              {mobileNavOpen ? <X size={16} /> : <Menu size={16} />}
            </button>

            <div className="relative shrink-0">
              <button
                onClick={() => setOpenMenu((m) => (m === "user" ? null : "user"))}
                className="flex items-center gap-2 border border-zinc-200 pl-1 pr-2 py-1 hover:border-[#002FA7] transition-colors"
                data-testid="user-menu-button"
              >
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt=""
                    className="w-7 h-7 rounded-full object-cover"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-[#002FA7] text-white flex items-center justify-center text-xs font-medium">
                    {(user.name || user.email || "?").charAt(0).toUpperCase()}
                  </div>
                )}
                <ChevronDown size={12} className="text-zinc-500" />
              </button>
              {openMenu === "user" && (
                <div
                  className="absolute right-0 top-full mt-1 w-64 border border-zinc-200 bg-white shadow-lg z-50"
                  data-testid="user-menu"
                >
                  <div className="px-4 py-3 border-b border-zinc-100">
                    <div className="text-sm font-medium text-zinc-900 truncate">{user.name || user.email}</div>
                    <div className="text-[11px] text-zinc-500 truncate">{user.email}</div>
                    {firm && (
                      <div className="text-[10px] tracking-wider text-zinc-500 mt-1.5 uppercase">
                        {firm.name}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => { setOpenMenu(null); navigate("/settings/team"); }}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-zinc-50 flex items-center gap-2 text-zinc-700"
                    data-testid="nav-team-settings"
                  >
                    <Users size={14} />
                    Team & invitations
                  </button>
                  {isSuperAdmin && (
                    <button
                      onClick={() => { setOpenMenu(null); navigate("/admin"); }}
                      className="w-full text-left px-4 py-2.5 text-sm hover:bg-zinc-50 flex items-center gap-2 text-[#002FA7] font-medium border-t border-zinc-100"
                      data-testid="nav-super-admin"
                    >
                      <Shield size={14} />
                      Super Admin Panel
                    </button>
                  )}
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-zinc-50 flex items-center gap-2 text-zinc-700 border-t border-zinc-100"
                    data-testid="logout-button"
                  >
                    <LogOut size={14} />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Mobile slide-down nav — flat list with subtle group labels */}
      {user && mobileNavOpen && (
        <nav
          className="md:hidden border-t border-zinc-200 bg-white"
          data-testid="mobile-nav"
        >
          <div className="max-w-7xl mx-auto px-4 py-2">
            {/* Standalone tabs first */}
            {NAV_ITEMS.filter((t) => !t.group && t.to !== "/notifications").map((t) => (
              <MobileNavLink key={t.to} item={t} onClick={() => setMobileNavOpen(false)} />
            ))}

            {/* Drafting group */}
            <div className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-[0.18em] text-zinc-400 font-medium">
              Drafting
            </div>
            {NAV_ITEMS.filter((t) => t.group === "drafting").map((t) => (
              <MobileNavLink key={t.to} item={t} onClick={() => setMobileNavOpen(false)} />
            ))}

            {/* Records group */}
            <div className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-[0.18em] text-zinc-400 font-medium">
              Records
            </div>
            {NAV_ITEMS.filter((t) => t.group === "records").map((t) => (
              <MobileNavLink key={t.to} item={t} onClick={() => setMobileNavOpen(false)} />
            ))}

            {/* Updates at the bottom */}
            <div className="border-t border-zinc-100 mt-2 pt-1">
              {NAV_ITEMS.filter((t) => t.to === "/notifications").map((t) => (
                <MobileNavLink key={t.to} item={t} onClick={() => setMobileNavOpen(false)} />
              ))}
            </div>
          </div>
        </nav>
      )}
    </header>
  );
}

function MobileNavLink({ item, onClick }) {
  const location = useLocation();
  const Icon = item.icon;
  const active =
    location.pathname === item.to || (item.to !== "/" && location.pathname.startsWith(item.to));
  return (
    <NavLink
      to={item.to}
      onClick={onClick}
      data-testid={`${item.testId}-mobile`}
      className={`flex items-center gap-3 px-3 py-3 text-sm font-medium border-l-2 transition-colors ${
        active
          ? "border-[#002FA7] text-[#002FA7] bg-blue-50/40"
          : "border-transparent text-zinc-700 hover:text-[#002FA7] hover:bg-zinc-50"
      }`}
    >
      <Icon size={16} strokeWidth={1.75} />
      {item.label}
    </NavLink>
  );
}
